/**
 * Mixin for synchronizing tab selection with URL query parameters.
 * Allows for navigation to page with a tab pre-selected.
 *
 * Usage:
 * 1. Add this mixin to your component
 * 2. Define `mixin_tab_config` in `data()` with your tab mapping
 * 3. Use `active_tab_index` as your v-model for tabs
 *
 * Example:
 * ```javascript
 * data() {
 *   return {
 *     mixin_tab_config: {
 *       query_param: 'tab', // optional, defaults to 'tab'
 *       default_tab_index: 0, // optional, defaults to 0
 *       tab_mapping: { // the keys here will be the query parameter values
 *         overview: 0,
 *         pricing: 1,
 *         permissions: 2,
 *         versions: 3
 *       }
 *     }
 *   }
 * }
 * ```
 */
export default {
  data() {
    return {
      active_tab_index: 0,
    };
  },

  computed: {
    tab_names() {
      if (!this.mixin_tab_config?.tab_mapping) return [];

      // index -> name
      const reverseMapping = {};
      Object.entries(this.mixin_tab_config.tab_mapping).forEach(([name, index]) => {
        reverseMapping[index] = name;
      });

      // index corresponds to tab name
      const maxIndex = Math.max(...Object.values(this.mixin_tab_config.tab_mapping));
      const names = [];
      for (let i = 0; i <= maxIndex; i++) {
        names[i] = reverseMapping[i] || null;
      }
      return names;
    },

    query_param_name() {
      return this.mixin_tab_config?.query_param || 'tab';
    },

    default_tab_index() {
      return this.mixin_tab_config?.default_tab_index ?? 0;
    },
  },

  methods: {
    setActiveTabFromQuery() {
      if (!this.mixin_tab_config?.tab_mapping) return;

      const tabParam = this.$route.query[this.query_param_name];
      if (!tabParam) {
        this.active_tab_index = this.default_tab_index;
        return;
      }

      const tabIndex = this.mixin_tab_config.tab_mapping[tabParam];
      // 0 would be falsy, so check undefined
      if (tabIndex !== undefined) {
        this.active_tab_index = tabIndex;
      }
    },

    updateUrlWithTab(tabIndex) {
      if (!this.tab_names.length) return;

      const tabName = this.tab_names[tabIndex];
      const currentParam = this.$route.query[this.query_param_name];

      if (currentParam === tabName) return;

      const query = { ...this.$route.query };

      // default tab, remove query param entirely
      if (tabIndex === this.default_tab_index || !tabName) {
        delete query[this.query_param_name];
      } else {
        query[this.query_param_name] = tabName;
      }

      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router
          .replace({
            name: this.$route.name,
            params: this.$route.params,
            query,
          })
          .catch(() => {
            // ignore 'navigation duplicated' errors
          });
      }
    },
  },

  mounted() {
    this.setActiveTabFromQuery();
  },

  watch: {
    '$route.query'() {
      this.setActiveTabFromQuery();
    },

    active_tab_index(newTabIndex) {
      this.updateUrlWithTab(newTabIndex);
    },
  },
};
